<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  tipoOperacao: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item', 'show-notification'])

const authStore = useAuthStore()
const uiStore = useUiStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'idLpcoComex', label: 'ID', width: 80, sortable: true, slotName: 'idLpcoComex' },
  { field: 'numeroLPCO', label: 'Nº LPCO', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'lstOrgaoAnuente', label: 'Órgão', sortable: true, slotName: 'orgao' },
  { field: 'lstModeloLPCO', label: 'Modelo', sortable: true, slotName: 'modelo' },
  { field: 'lstSentido', label: 'Sentido', align: 'center', sortable: true, slotName: 'sentido' },
  { field: 'lstStatusLPCO', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'dataValidade', label: 'Validade', align: 'center', sortable: true, slotName: 'dataValidade' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstOrgaoAnuente: (row) => getOrgaoTexto(row.lstOrgaoAnuente),
    lstModeloLPCO: (row) => getModeloTexto(row.lstModeloLPCO),
    lstSentido: (row) => getSentidoTexto(row.lstSentido),
    lstStatusLPCO: (row) => getStatusTexto(row.lstStatusLPCO)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const temPermissaoCompleta = computed(() => {
  if (authStore.isAdminInterno) return true
  if (authStore.pode('EMBARQUES_COMPLETO')) return true
  if (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO')) return true
  return authStore.getPermsDoModulo('EMBARQUES').some(p =>
    p === 'COMPLETO' || p.includes('COMPLETO')
  )
})
const temPermissaoVisualizacao = computed(() => authStore.pode('EMBARQUES'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['LpcoComex'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('termo', filtros.busca)
    if (props.tipoOperacao) params.append('lstTipoOperacaoComex', props.tipoOperacao)

    const response = await api.get(`/LpcoComex?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os LPCOs.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarItem = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/LpcoComex/${item.idLpcoComex}`)
    const data = response.data
    // Enriquece o objeto com tipoOperacao para que o cadastro saiba de onde veio
    if (props.tipoOperacao) {
      data._tipoOperacao = props.tipoOperacao
    }
    emit('editar-item', data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do LPCO.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const excluirItem = async (item) => {
  try {
    await api.delete(`/LpcoComex/${item.idLpcoComex}`)
    emit('show-notification', { message: 'LPCO excluído com sucesso.', type: 'success' })
    buscarDados(paginacao.value.CurrentPage)
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir LPCO.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idLpcoComex
  auditoriaSelecionada.nome = item.numeroLPCO || `LPCO #${item.idLpcoComex}`
  mostrarAuditoria.value = true
}

const getOrgaoTexto = (id) => {
  const item = (listas.LstOrgaoAnuente || []).find(s => s.idListaClienteItem === id)
  return item ? item.textoLista : id
}

const getModeloTexto = (id) => {
  const item = (listas.LstModeloLPCO || []).find(s => s.idListaClienteItem === id)
  return item ? item.textoLista : id
}

const getSentidoTexto = (id) => {
  const item = (listas.LstSentido || []).find(s => s.idListaClienteItem === id)
  return item ? item.textoLista : id
}

const getStatusTexto = (id) => {
  const item = (listas.LstStatusLPCO || []).find(s => s.idListaClienteItem === id)
  return item ? item.textoLista : id
}

const obterCorStatus = (idStatus) => {
  const texto = String(getStatusTexto(idStatus)).toUpperCase()
  if (texto.includes('DEFERIDO')) return 'badge-ghost-success'
  if (texto.includes('INDEFERIDO')) return 'badge-ghost-danger'
  if (texto.includes('ANÁLISE') || texto.includes('DIGITAÇÃO')) return 'badge-ghost-warning'
  if (texto.includes('VENCIDO')) return 'badge-ghost-secondary'
  return 'badge-ghost-info'
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    buscarDados(1)
  }
})

onActivated(async () => {
  await carregarListas()
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    :titulo="uiStore.currentModuleTitle || 'Gestão de LPCO'"
    subtitulo="Licenças, Permissões, Certificados e Outros Documentos (Siscomex)"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Busca Geral</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Nº LPCO, Órgão, Modelo..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos os Status</option>
            <option v-for="s in (listas.LstStatusLPCO || [])" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum LPCO encontrado."
      row-key="idLpcoComex"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarItem($event.row)"
    >
      <template #idLpcoComex="{ row }">
        <span class="text-muted fw-medium">#{{ row.idLpcoComex }}</span>
      </template>

      <template #orgao="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getOrgaoTexto(row.lstOrgaoAnuente) }}
        </span>
      </template>

      <template #modelo="{ row }">
        {{ getModeloTexto(row.lstModeloLPCO) }}
      </template>

      <template #sentido="{ row }">
        <span class="badge bg-body-tertiary text-body border border-secondary-subtle">
          {{ getSentidoTexto(row.lstSentido) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusLPCO)">{{ getStatusTexto(row.lstStatusLPCO) }}</span>
      </template>

      <template #dataValidade="{ row }">
        {{ formatarDataBR(row.dataValidade) }}
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarItem(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
          <button v-if="row.lstStatusLPCO === 'LPCO_ST1'" class="btn-icon text-danger" @click="excluirItem(row)" title="Excluir">
            <i class="bi bi-trash-fill"></i>
          </button>
        </div>
      </template>

    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="LpcoComex"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    :tabelas-filhas="['LpcoComexItem']"
    chave-estrangeira="IdLpcoComex"
    @fechar="mostrarAuditoria = false"
  />
</template>
